/**
 * viewerUrl.ts
 *
 * Single source of truth for building viewer URLs (iframe-rendered or direct
 * navigation) and resolving the app base path.
 *
 * Consolidated from:
 *   - src/pages/PublicDriveEmbedPage.tsx (was duplicated buildViewerUrl + appBasePath)
 *   - src/pages/EmbedRedirectPage.tsx    (was duplicated buildViewerUrl + appBasePath)
 *
 * Why this exists: both pages built the SAME viewer URL using the SAME logic
 * but with character-for-character copy-paste. Any future change had to be
 * made in two places. Consolidating eliminates drift risk.
 *
 * Future consumers:
 *   - src/viewer/ViewerPanel.tsx (PR 2) — for iframe-fallback rendering
 *   - src/pages/StandaloneEmbedShellPage.tsx (PR 2) — for the public /embed/{id} route
 *   - src/viewer/components/CopyEmbedButton.tsx (PR 3) — for copy-snippet generation
 */

import { getApiBase } from '@services/api/apiConfig';
import type { EmbedShare } from '@/types/embed.types';

const DEFAULT_GENESIS_VIEW_BASE_URL = 'https://view.genesisframeworks.com/';
const PDF_MANIFEST_TYPE = 'pdf';
const PROXY_DELIVERY_MODE = 'proxy';

/**
 * Returns the app's base URL path, normalized to always end with `/`.
 *
 * Reads `import.meta.env.BASE_URL` (set by Vite based on `vite.config.ts`).
 * Defaults to `/` if not set or empty.
 */
export function appBasePath(): string {
  const base = import.meta.env.BASE_URL || '/';
  return base.endsWith('/') ? base : `${base}/`;
}

function normalizeViewerBaseUrl(baseUrl = DEFAULT_GENESIS_VIEW_BASE_URL): string {
  const value = String(baseUrl || DEFAULT_GENESIS_VIEW_BASE_URL).trim() || DEFAULT_GENESIS_VIEW_BASE_URL;
  return value.endsWith('/') ? value : `${value}/`;
}

function buildManifestUrl(share: EmbedShare): string {
  const manifestType = share.viewType === 'gallery' ? 'image' : PDF_MANIFEST_TYPE;
  const url = new URL(`/api/public/embed/${encodeURIComponent(share.id)}/manifest`, `${getApiBase()}/`);
  url.searchParams.set('type', manifestType);
  if (manifestType === PDF_MANIFEST_TYPE) {
    url.searchParams.set('delivery', PROXY_DELIVERY_MODE);
  }
  return url.toString();
}

/**
 * Builds the viewer URL for an embed share.
 *
 * URL shape:
 *   - gallery viewType    -> {basePath}view/images/gallery.html?source={manifest}&title={title}
 *   - pdf-library viewType -> https://view.genesisframeworks.com/library.html?library={manifest}
 *   - pdf-reader viewType  -> https://view.genesisframeworks.com/smart.html?library={manifest}&sidebar=0
 *   - default (other)      -> https://view.genesisframeworks.com/smart.html?library={manifest}
 *
 * The manifest URL points at the public embed manifest endpoint
 * (`/api/public/embed/{shareId}/manifest?type={image|pdf}`). PDF manifests
 * always request `delivery=proxy`, matching the Drive file-open resolver and
 * keeping PDF CORS/range behavior owned by the Box/View protocol instead of
 * the stale vendored View fallback.
 *
 * @param share - The embed share to build a viewer URL for.
 * @returns Absolute URL string suitable for use as iframe `src` or window.location.
 */
export function buildViewerUrl(share: EmbedShare): string {
  const manifestUrl = buildManifestUrl(share);

  if (share.viewType === 'gallery') {
    const url = new URL(`${appBasePath()}view/images/gallery.html`, window.location.origin);
    url.searchParams.set('source', manifestUrl);
    url.searchParams.set('title', share.title);
    return url.toString();
  }

  const route = share.viewType === 'pdf-library' ? 'library.html' : 'smart.html';
  const url = new URL(route, normalizeViewerBaseUrl());
  url.searchParams.set('library', manifestUrl);
  if (share.viewType === 'pdf-reader') {
    url.searchParams.set('sidebar', '0');
  }

  return url.toString();
}
